export const BUTTON_VARIANTS = [
  "default",
  "secondary",
  "outline",
  "ghost",
  "destructive",
  "dark",
  "premium",
  "surface",
] as const;

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

export const BUTTON_SIZES = ["default", "sm", "lg", "icon"] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

export const buttonVariantClassName: Record<ButtonVariant, string> = {
  default: "bg-primary text-primary-foreground shadow-sm hover:brightness-95",
  secondary: "bg-secondary text-secondary-foreground hover:bg-muted",
  dark: "bg-foreground text-background shadow-sm hover:opacity-90",
  premium:
    "bg-linear-to-r from-premium-500 to-premium-600 text-primary-foreground shadow-lg hover:brightness-105",
  surface: "bg-card text-foreground border border-border hover:bg-muted/50",
  outline: "border border-border bg-transparent text-foreground hover:bg-muted",
  ghost: "bg-transparent text-foreground hover:bg-muted",
  destructive:
    "bg-destructive text-destructive-foreground shadow-sm hover:brightness-110",
};

export const buttonSizeClassName: Record<ButtonSize, string> = {
  default: "h-10 px-4 py-2",
  sm: "h-9 rounded-md px-3 text-xs",
  lg: "h-12 rounded-xl px-8 text-base",
  icon: "h-10 w-10",
};

export const buttonBaseClassName =
  "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50";

export const buttonTextClassName: Record<ButtonVariant, string> = {
  default: "text-primary-foreground",
  secondary: "text-secondary-foreground",
  dark: "text-background",
  premium: "text-primary-foreground",
  surface: "text-foreground",
  outline: "text-foreground",
  ghost: "text-foreground",
  destructive: "text-destructive-foreground",
};

export const ALERT_VARIANTS = [
  "default",
  "success",
  "error",
  "warning",
] as const;
export type AlertVariant = (typeof ALERT_VARIANTS)[number];

export const alertVariantClassName: Record<AlertVariant, string> = {
  default: "border-border bg-card text-card-foreground",
  success: "border-success/20 bg-success/10 text-success",
  error: "border-destructive/20 bg-destructive/10 text-destructive",
  warning: "border-accent/30 bg-accent/10 text-accent",
};

export const BADGE_VARIANTS = [
  "default",
  "secondary",
  "outline",
  "premium",
  "on-dark",
] as const;
export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export const badgeVariantClassName: Record<BadgeVariant, string> = {
  default: "bg-primary/10 text-primary",
  secondary: "bg-secondary text-secondary-foreground",
  outline: "border border-border text-foreground",
  premium: "bg-premium-500/10 text-premium-400 border border-premium-500/20",
  "on-dark": "border border-border bg-card text-foreground",
};

export const TEXT_TONES = ["default", "muted", "on-dark"] as const;
export type TextTone = (typeof TEXT_TONES)[number];

export const textToneClassName: Record<TextTone, string> = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  "on-dark": "text-background/80",
};

export const TEXT_SIZES = ["sm", "md", "lg"] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

export const textSizeClassName: Record<TextSize, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
};

export const HEADING_SIZES = ["display", "xl", "lg", "md"] as const;
export type HeadingSize = (typeof HEADING_SIZES)[number];

export const headingSizeClassName: Record<HeadingSize, string> = {
  display: "text-4xl font-semibold tracking-tight",
  xl: "text-3xl font-semibold tracking-tight",
  lg: "text-2xl font-semibold tracking-tight",
  md: "text-xl font-semibold tracking-tight",
};

export const inputClassName =
  "flex h-11 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground";

export const textareaClassName =
  "flex min-h-24 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground";

export const labelClassName = "text-sm font-medium text-foreground";

export const cardClassName =
  "rounded-xl border border-border bg-card text-card-foreground";
